/**
 * device-plugin — the `/device` genome-native IoT Device surface as a studio-sibling
 * `ComposableGenomePlugin` (#515, the KEYSTONE surface).
 *
 * Mirrors `slicerPlugin`: one `component` editor (`device`) routed at `/device`,
 * lowering onto `component`/`route` only (NO new `EntityKind` — a Device is a genome
 * DOMAIN-PATTERN, not an engine primitive; gate 4 stays green, the route count
 * derives). The editor is `React.lazy` so the whole device stack (the adapters, the
 * views, the panel-renderer registry, the context contracts) stays in a split chunk
 * OUT of the app entry (gate 8 — the eager module carries only the route + the
 * `React.lazy` boundary).
 *
 * The RIGHT-region IDE dock panel is registered SEPARATELY by `device-dock.tsx`,
 * imported as a side-effect from the LAZY `IdeShell` (`/ide` chunk) — so its
 * Suspense-wrapper + registration weight rides the IDE chunk, NOT the eager app
 * entry (the #288/#482c Commandment-17 reclaim: the device chain pays its own
 * gate-8 tax). The route surface `/device` works regardless of the dock.
 *
 * @module @ubiclouds/v5-studio/device/device-plugin
 */

import { lazy } from 'react';
import { definePlugin } from '@ubiclouds/v5-sdk/plugins';
import type { ComposableGenomePlugin } from '@ubiclouds/v5-sdk/plugins';

/** The Device-surface plugin id. */
export const DEVICE_PLUGIN_ID = 'v5-studio-device';

/** The split-chunk Device surface (the `/device` route editor + the dock panel). */
export const LazyDeviceSurface = lazy(() => import('./DeviceSurface.js'));

/**
 * The Device plugin — the `/device` route → the lazy `device` editor. Composed as a
 * SIBLING in `studioPlugins`; it adds no new `EntityKind` (the surface lowers onto
 * `component`/`route`), so gate 4 stays green.
 */
export const devicePlugin: ComposableGenomePlugin = definePlugin(DEVICE_PLUGIN_ID)
  .name('Device')
  .trustTier('core')
  .editor('device', LazyDeviceSurface)
  .route('/device', { routesTo: 'component/device', title: 'Device' })
  .build();
